// HTTP-клиент приложения: тонкая обёртка над fetch с базовым URL из окружения.
//
// Почему fetch, а не axios: клиент живёт в WebView Capacitor-оболочки, где fetch
// доступен нативно, а лишняя зависимость в бандле — лишние килобайты на кухонном
// планшете. Контракт обёртки тот же, что описан в app.md §5.
//
// Ключевое различие, на котором держится офлайн-слой: ответ сервера с кодом ошибки
// (HttpError) и недоставленный запрос (NetworkError) — разные вещи. Первый означает
// «сервер ответил, операцию повторять бессмысленно», второй — «сети нет, операция
// остаётся в очереди и будет повторена» (offline-first.md §5). Склеивать их в одну
// ошибку нельзя: так теряются печати.

import { authHeader } from '@/shared/auth';

import { clientVersionHeader } from './clientVersion';
import { getApiBaseUrl } from './serverAddress';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type QueryParamValue = string | number | boolean | null | undefined;

export type HttpRequestOptions = {
  readonly method?: HttpMethod;
  readonly body?: unknown;
  readonly searchParams?: Readonly<Record<string, QueryParamValue>>;
  readonly headers?: Readonly<Record<string, string>>;
  readonly signal?: AbortSignal;
};

/** Сервер ответил статусом вне 2xx. Тело ответа сохраняется для user-friendly сообщения. */
export class HttpError extends Error {
  constructor(
    readonly status: number,
    readonly statusText: string,
    readonly payload: unknown,
  ) {
    super(`HTTP ${status} ${statusText}`);
    this.name = 'HttpError';
  }
}

/** Запрос не долетел (нет сети, обрыв, таймаут). Для очереди синхронизации — повод для retry. */
export class NetworkError extends Error {
  constructor(readonly originalError: unknown) {
    super('Запрос не доставлен: нет соединения', { cause: originalError });
    this.name = 'NetworkError';
  }
}

/**
 * Запрос не дошёл до сервера (сеть, таймаут, прокси) или сервер упал — стоит повторить.
 * Ответ 4xx — решение сервера, его не повторяют. Одно правило на все фоновые повторы
 * (push при входе, отложенный выход): разъехавшись, они спорили бы об одной ошибке.
 */
export function isUndelivered(error: unknown): boolean {
  return error instanceof NetworkError || (error instanceof HttpError && error.status >= 500);
}

function buildUrl(path: string, searchParams?: Readonly<Record<string, QueryParamValue>>): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  // Базовый URL — рантайм-значение (адрес сервера сети вводит оператор, Фаза 4).
  const url = `${getApiBaseUrl()}${normalizedPath}`;

  if (!searchParams) {
    return url;
  }

  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(searchParams)) {
    if (value === undefined || value === null) {
      continue;
    }
    query.append(key, String(value));
  }

  const queryString = query.toString();
  return queryString ? `${url}?${queryString}` : url;
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204) {
    return undefined;
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    return (await response.json()) as unknown;
  }

  return await response.text();
}

async function request<TResponse>(
  path: string,
  options: HttpRequestOptions = {},
): Promise<TResponse> {
  const { method = 'GET', body, searchParams, headers, signal } = options;
  // Путь нормализуется здесь же: `authHeader` решает по нему, публичная это ручка
  // или нет, и получить его из `buildUrl` нельзя — тот возвращает готовый URL.
  const requestPath = path.startsWith('/') ? path : `/${path}`;

  const init: RequestInit = {
    method,
    // credentials НЕ 'include' — см. app.md §12. Сессия ездит в заголовке Authorization,
    // а не в cookie: API кросс-origin по построению (WebView оболочки живёт на
    // https://localhost), и cookie для него всегда третьесторонняя. Возврат 'include'
    // здесь — регресс, а не «как обычно»; на сервере CORS_ALLOW_CREDENTIALS = False,
    // и запрос с credentials просто перестанет проходить.
    headers: {
      Accept: 'application/json',
      ...authHeader(requestPath),
      // Сервер пускает его в CORS_ALLOW_HEADERS (`settings/base.py`): без этого
      // preflight отклонил бы КАЖДЫЙ запрос, а не одну ручку.
      ...clientVersionHeader(),
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...headers,
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    ...(signal ? { signal } : {}),
  };

  let response: Response;
  try {
    response = await fetch(buildUrl(path, searchParams), init);
  } catch (error) {
    // fetch бросает только когда запрос не доставлен: обрыв, отсутствие сети, abort.
    throw new NetworkError(error);
  }

  // Время сервера из заголовка `Date` — бесплатный и единственный надёжный источник
  // для замера дрейфа часов. Отдельного запроса «который час» нет намеренно: он был бы
  // лишним обращением на плохой сети ради данных, которые и так приходят с каждым ответом.
  //
  // Слушателем, а не прямым вызовом: транспорт не должен знать про офлайн-слой,
  // иначе `httpClient` становится непроверяемым без Dexie.
  const serverDate = response.headers.get('date');
  if (serverDate) {
    notifyServerTime(new Date(serverDate));
  }

  const payload = await parseBody(response);

  if (!response.ok) {
    // 502/503/504 от локального прокси оболочки (desktop/src/webServer.ts) означают
    // «до бэкенда не достучались», а не решение сервера по запросу. Для клиента это
    // тот же класс, что обрыв сети: запрос не доставлен → можно отдать данные из
    // кэша и не выкидывать человека на /login (иначе оффлайн в оболочке не работает).
    if (response.status === 502 || response.status === 503 || response.status === 504) {
      throw new NetworkError(new Error(`upstream ${response.status}`));
    }
    throw new HttpError(response.status, response.statusText, payload);
  }

  return payload as TResponse;
}

/**
 * Подписка на время сервера.
 *
 * Дрейф часов на кухонном планшете — не теория: на нём считается СРОК ГОДНОСТИ,
 * и сбитые часы дают неверную дату на стикере. Замер живёт в `offline/clock`,
 * а слушателя регистрирует он же.
 */
const serverTimeListeners = new Set<(at: Date) => void>();

export function onServerTime(listener: (at: Date) => void): () => void {
  serverTimeListeners.add(listener);
  return () => {
    serverTimeListeners.delete(listener);
  };
}

function notifyServerTime(at: Date): void {
  for (const listener of serverTimeListeners) {
    listener(at);
  }
}

export const httpClient = {
  get: <TResponse>(path: string, options?: Omit<HttpRequestOptions, 'method' | 'body'>) =>
    request<TResponse>(path, { ...options, method: 'GET' }),

  post: <TResponse>(path: string, body?: unknown, options?: Omit<HttpRequestOptions, 'method'>) =>
    request<TResponse>(path, { ...options, method: 'POST', body }),

  put: <TResponse>(path: string, body?: unknown, options?: Omit<HttpRequestOptions, 'method'>) =>
    request<TResponse>(path, { ...options, method: 'PUT', body }),

  patch: <TResponse>(path: string, body?: unknown, options?: Omit<HttpRequestOptions, 'method'>) =>
    request<TResponse>(path, { ...options, method: 'PATCH', body }),

  delete: <TResponse>(path: string, options?: Omit<HttpRequestOptions, 'method' | 'body'>) =>
    request<TResponse>(path, { ...options, method: 'DELETE' }),
} as const;
